import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Music, Play, Loader2, AlertTriangle, Crown, Check, RefreshCw } from 'lucide-react';
import { validateVipLink, triggerVipSong } from '../../services/vipTrigger';
import { getRequestsByEventId, type SongRequest } from '../../services/requests';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FirebaseError } from 'firebase/app';
import { isAlreadyHandledError, shareLinkErrorMessage } from '../../utils/shareLinks';

export default function VipTriggerPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [eventName, setEventName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [songs, setSongs] = useState<SongRequest[]>([]);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!eventId || !token) {
      setError('Invalid VIP link');
      setLoading(false);
      return;
    }

    const validate = async () => {
      try {
        const validSessionId = await validateVipLink(eventId, token);

        // Get event name. A deleted event's links stop working (ITER-154)
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (!eventDoc.exists() || eventDoc.data().deletedAt != null) {
          setError('VIP session not found or has been revoked');
          return;
        }
        setSessionId(validSessionId);
        setEventName(eventDoc.data().name || 'Event');
      } catch (validateError) {
        console.error('VIP link validation failed', validateError);
        if (validateError instanceof FirebaseError && validateError.code === 'functions/permission-denied') {
          setError('VIP session not found or has been revoked');
        } else {
          setError('Failed to connect');
        }
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [eventId, token]);

  // Fetch VIP-triggerable songs
  const fetchSongs = useCallback(async () => {
    if (!eventId || !sessionId) return;
    try {
      const all = await getRequestsByEventId(eventId);
      const vipSongs = all.filter(r =>
        r.vipTriggerable &&
        r.status === 'queued' &&
        !(r.vipOneTime && r.vipTriggered && !r.vipPriority) // Keep showing if it's the active trigger
      );
      setSongs(vipSongs);

      // If a one-time song was triggered and already played (no longer in queue), it won't appear
    } catch {
      // Will retry on next poll
    }
  }, [eventId, sessionId]);

  // Poll for songs
  useEffect(() => {
    if (!sessionId) return;
    fetchSongs();
    const interval = setInterval(fetchSongs, 4000);
    return () => clearInterval(interval);
  }, [sessionId, fetchSongs]);

  const handleTrigger = async (requestId: string) => {
    if (!eventId || !token) return;
    setTriggering(true);
    try {
      await triggerVipSong({ eventId, token, requestId });

      setConfirmId(null);

      // Update local state to reflect changes
      const triggeredSong = songs.find(s => s.id === requestId);
      setSongs(prev => prev.map(s => {
        if (s.id === requestId) return { ...s, vipTriggered: true, vipPriority: true };
        return { ...s, vipPriority: false };
      }));
      toast.success(`"${triggeredSong?.song.title}" will play next!`);
    } catch (err) {
      console.error('VIP trigger failed:', err);
      toast.error(shareLinkErrorMessage(err, 'VIP'));
      if (isAlreadyHandledError(err)) {
        fetchSongs();
      }
    } finally {
      setTriggering(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSongs();
    setRefreshing(false);
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-base-content/60">Connecting...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-base-content/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-b border-amber-500/30 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Crown className="w-6 h-6 text-amber-500" />
          <div>
            <h1 className="text-lg font-bold">VIP Trigger</h1>
            <p className="text-sm text-base-content/60">{eventName}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="ml-auto btn btn-ghost btn-sm btn-circle"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Song List */}
      <div className="max-w-lg mx-auto p-4 space-y-3">
        {songs.length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-12 h-12 text-base-content/20 mx-auto mb-3" />
            <p className="text-base-content/60 font-medium">No songs available</p>
            <p className="text-sm text-base-content/40 mt-1">
              The DJ hasn't marked any songs for VIP triggering yet.
            </p>
          </div>
        ) : (
          songs.map(song => (
            <div
              key={song.id}
              className={`rounded-lg border p-4 ${confirmId === song.id ? 'relative z-50' : ''} ${
                song.vipPriority && song.vipTriggered
                  ? 'bg-amber-500/15 border-amber-500/40 ring-2 ring-amber-500/30'
                  : song.vipTriggered
                    ? 'bg-success/10 border-success/30'
                    : 'bg-base-200/50 border-base-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Album Art */}
                {song.song.albumArtUrl ? (
                  <img
                    src={song.song.albumArtUrl}
                    alt=""
                    className="w-14 h-14 rounded-md object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-base-300 flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-base-content/30" />
                  </div>
                )}

                {/* Song Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{song.song.title}</p>
                  <p className="text-sm text-base-content/60 truncate">{song.song.artist}</p>
                  {song.vipOneTime && (
                    <span className="text-xs text-amber-500">One-time only</span>
                  )}
                </div>

                {/* Action */}
                {song.vipPriority && song.vipTriggered ? (
                  <div className="flex items-center gap-1 text-amber-500 text-sm font-bold animate-pulse">
                    <Crown className="w-4 h-4" />
                    Playing Next
                  </div>
                ) : song.vipTriggered && !song.vipPriority ? (
                  <div className="flex items-center gap-1 text-success text-sm font-medium">
                    <Check className="w-4 h-4" />
                    Played
                  </div>
                ) : confirmId === song.id ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTrigger(song.id)}
                      disabled={triggering}
                      className="btn btn-sm btn-primary"
                    >
                      {triggering ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Confirm'
                      )}
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="btn btn-sm btn-ghost"
                      disabled={triggering}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(song.id)}
                    className="btn btn-sm border-none bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <Play className="w-4 h-4" />
                    Play Next
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Confirmation Modal Overlay */}
      {confirmId && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => !triggering && setConfirmId(null)}
        />
      )}
    </div>
  );
}
